import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
import { useDispatch, useSelector } from 'react-redux'
import {
  change,
  reduxForm,
  arrayPush,
  initialize,
  arrayRemove,
  clearFields,
  arrayRemoveAll,
  formValueSelector,
  InjectedFormProps
} from 'redux-form'
// components
import { Header } from 'components/Modals/PlanItems/styles'
import { Button, BUTTON_LAYOUTS, DrawerModal } from 'components/UI'
// view components
import Field from './components/Field'
import Fields from './components/Fields'
import Filters from './components/Filters'
// themes
import { CloseIcon } from 'components/UI/Icons'
// constants
import { Forms, Modals } from 'constants/index'
// hooks
import { useRelatedFields } from 'hooks/risks/useRelatedFields'
import { useCustomColumnFields } from 'hooks/risks/useCustomColumnFields'
import { useModalData, useShowModal } from 'hooks/index'
// context
import { RiskViewAPIContext } from 'containers/Risks/RiskView/layers/API'
import { RiskRegisterContext } from 'containers/Risks/RisksRegister/layers'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// icons
import PlusIcon from '@mui/icons-material/Add'
import IconButton from '@mui/material/IconButton'
// themes
import { colors } from 'themes/index'
// types
import { FieldType, IField, IFieldFilter } from 'types/graphql'
// graphql
import { RISK_VIEW_FILTER_UPDATE_MUTATION } from 'graphql/risk/mutations'
// views
import { ButtonsWrapper, Container, Wrapper } from 'components/Modals/RiskViewFilterModal/views'
// utils
import { uniqBy } from 'lodash'
import { handleError } from 'utils/errors/handlers'
import { filterByRiskView } from 'utils/form'
import { generateFieldFilter, generateFieldValueFromFilter } from './utils'

const PAPER_PROPS = { style: { overflow: 'hidden' } }

export type FilterField = IFieldFilter & { name: string }

const selector = formValueSelector(Forms.RISK_VIEW_FILTER_FORM)

export const FIELD_TYPES_TO_EXCLUDE = [
  FieldType.RiskMatrix,
  FieldType.Table,
  FieldType.DocumentOrLink,
  FieldType.Section,
  FieldType.Label,
  FieldType.InjuryMasterLinks,
  FieldType.InjuryMasterParentLinks,
  FieldType.ParentRelatedForms,
  FieldType.RecordLinks
]

const ARCHIVED_FIELD: IField = {
  id: 'ARCHIVED',
  label: 'Archived',
  fieldType: FieldType.Switch,
  multiple: false
}

const RiskViewFilterDrawer: React.FC<InjectedFormProps<any>> = () => {
  const [updateFilterMutation] = useMutation(RISK_VIEW_FILTER_UPDATE_MUTATION)

  const [currentField, setCurrentField] = React.useState<(IField & { idx?: number }) | null>(null)
  const [isConfigured, setIsConfigured] = React.useState(false)

  const { riskView } = React.useContext(RiskRegisterContext)
  const { riskTemplate } = React.useContext(RiskTemplateAPIContext)
  const { appliedFilters, setAppliedFilters, currentTab, setCurrentTab, order, setOrder, filtersRefetch } =
    React.useContext(RiskViewAPIContext)

  const dispatch = useDispatch()

  const filter = useSelector((state) => selector(state, 'filter') || [])
  const search = useSelector((state) => selector(state, 'search') || '')

  const { showModal } = useShowModal({ modalType: Modals.RISK_VIEW_ADD_FILTER_MODAL })

  const {
    hideModal,
    modalData: { isOpen, isClone, data: filterData }
  } = useModalData({ modalType: Modals.RISK_VIEW_FILTER_MODAL })

  const { relatedFields } = useRelatedFields()

  const { columnFields } = useCustomColumnFields()

  const fields = React.useMemo(() => {
    const templateFields = uniqBy([...riskTemplate?.fields, ...relatedFields], 'id')
      .filter((field) => !FIELD_TYPES_TO_EXCLUDE.includes(field.fieldType) && filterByRiskView(field, riskView))
      .map((field) => ({ ...field, multiple: false }))

    // A custom column is offered as a single free-text filter — it searches every source it reads
    // from regardless of that source's own type — and it's not part of settings.fields, so it is
    // appended instead of filtered by the view.
    const customColumnFields = columnFields.map((field) => ({ ...field, fieldType: FieldType.Text }))

    return [ARCHIVED_FIELD, ...templateFields, ...customColumnFields]
  }, [riskTemplate, relatedFields, columnFields, riskView])

  // The search narrows only the list to pick a field from — the rules are built against every field,
  // otherwise a rule whose field doesn't match the search can't be built and the whole list fails
  const searchedFields = React.useMemo(
    () => (search ? fields.filter((field) => field.label.toLowerCase().includes(search.toLowerCase())) : fields),
    [fields, search]
  )

  // methods
  const addFilter = (field: FilterField) => {
    setCurrentField(null)
    setIsConfigured(false)

    if (currentField.idx || currentField.idx === 0) {
      dispatch(change(Forms.RISK_VIEW_FILTER_FORM, `filter[${currentField.idx}]`, field))
    } else {
      dispatch(arrayPush(Forms.RISK_VIEW_FILTER_FORM, 'filter', field))
    }

    dispatch(clearFields(Forms.RISK_VIEW_FILTER_FORM, false, false, currentField.id))
  }

  const removeFilter = (idx: number) => {
    dispatch(arrayRemove(Forms.RISK_VIEW_FILTER_FORM, 'filter', idx))
  }

  const clearFilters = () => {
    setCurrentField(null)
    setIsConfigured(false)
    dispatch(arrayRemoveAll(Forms.RISK_VIEW_FILTER_FORM, 'filter'))
  }

  const closeDrawer = () => {
    hideModal()
    clearFilters()
  }

  const clearField = () => {
    setCurrentTab(null)

    if (currentField.idx || currentField.idx === 0) {
      setIsConfigured(false)
      setCurrentField(null)
    } else {
      setCurrentField(null)
    }

    dispatch(clearFields(Forms.RISK_VIEW_FILTER_FORM, false, false, currentField.id))
  }

  const handleSetField = (field: IField & { idx?: number }) => {
    setCurrentField(field)
  }

  // Rules whose field is gone are dropped: their value can't be rebuilt without the field
  const getFieldFilters = () => filter.map((item) => generateFieldFilter(item, fields)).filter(Boolean)

  const applyFilters = () => {
    const fieldFilters = getFieldFilters()

    setAppliedFilters(fieldFilters)

    if (currentTab) {
      setCurrentTab(null)
    }

    closeDrawer()
  }

  const onClickSave = () => {
    const fieldFilters = getFieldFilters()

    showModal({ fieldFilters, targetId: riskTemplate.id })
  }

  const onClickClear = () => {
    clearFilters()
    setOrder(null)
    setAppliedFilters(null)
    setCurrentTab(null)
  }

  const updateFilter = async () => {
    try {
      const fieldFilters = getFieldFilters()

      const args = {
        ...filterData.args,
        fieldFilters,
        ...(currentTab === filterData.id && { order })
      }

      if (riskView.id) {
        await updateFilterMutation({
          variables: {
            input: {
              args,
              id: filterData.id
            }
          }
        })

        filtersRefetch()
      }

      if (currentTab === filterData.id) {
        setAppliedFilters(fieldFilters)
      }

      closeDrawer()
    } catch (e) {
      handleError(e, 'RiskViewFilterDrawer.updateFilter')
    }
  }

  React.useEffect(() => {
    if (isOpen && filterData) {
      const filter = filterData.args.fieldFilters?.map((filter) => generateFieldValueFromFilter(filter, fields))
      dispatch(initialize(Forms.RISK_VIEW_FILTER_FORM, { filter }))
      return
    }

    if (isOpen && appliedFilters) {
      const filter = appliedFilters.map((filter) => generateFieldValueFromFilter(filter, fields))
      dispatch(initialize(Forms.RISK_VIEW_FILTER_FORM, { filter }))
    }
  }, [isOpen, filterData, appliedFilters])

  const showAddedFilters = !!filter.length && !isConfigured
  const showFiltersList = isConfigured && !currentField
  const showFieldSettings = currentField
  const showPlaceholder = !filter.length && !isConfigured

  return (
    <DrawerModal
      data-testid='risk-view-filter-drawer'
      width='400px'
      isOpen={isOpen}
      hideModal={hideModal}
      PaperProps={PAPER_PROPS}>
      <Header>
        <span>Filter</span>
        <CloseIcon onClick={closeDrawer} />
      </Header>
      <Container>
        {showPlaceholder && (
          <Wrapper>
            <div>This view has no advanced filters.</div>
            <IconButton data-testid='risk-filter-add-rule-button' onClick={() => setIsConfigured(true)} size='large'>
              <PlusIcon htmlColor={colors.purple500} />
            </IconButton>
          </Wrapper>
        )}

        {showAddedFilters && (
          <Filters
            data={filter}
            fields={fields}
            setField={handleSetField}
            removeFilter={removeFilter}
            setIsConfigured={setIsConfigured}
          />
        )}

        {showFiltersList && (
          <Fields filter={filter} fields={searchedFields} setField={handleSetField} setIsConfigured={setIsConfigured} />
        )}

        {showFieldSettings && <Field field={currentField} addField={addFilter} clearField={clearField} />}

        {filterData ? (
          <ButtonsWrapper isUpdate>
            <Button type='button' layout={BUTTON_LAYOUTS.PURPLE_INVERTED} onClick={closeDrawer}>
              Cancel
            </Button>
            <Button type='button' layout={BUTTON_LAYOUTS.PURPLE} onClick={isClone ? onClickSave : updateFilter}>
              {isClone ? 'Clone' : 'Update'}
            </Button>
          </ButtonsWrapper>
        ) : (
          <ButtonsWrapper>
            <Button
              type='button'
              onClick={onClickClear}
              disabled={!filter.length}
              layout={BUTTON_LAYOUTS.PURPLE_INVERTED}>
              Clear
            </Button>
            <Button type='button' onClick={applyFilters} disabled={!filter.length} layout={BUTTON_LAYOUTS.PURPLE}>
              Apply
            </Button>
            <Button type='button' layout={BUTTON_LAYOUTS.PURPLE} onClick={onClickSave} disabled={!filter.length}>
              Save
            </Button>
          </ButtonsWrapper>
        )}
      </Container>
    </DrawerModal>
  )
}

export default reduxForm<any, any>({
  form: Forms.RISK_VIEW_FILTER_FORM,
  destroyOnUnmount: false,
  enableReinitialize: true
})(RiskViewFilterDrawer)
